
import { Component, Vue, Watch, Prop, Provide } from "vue-property-decorator"
import { namespace, Getter, State } from "vuex-class"

import api from "@/services/api"
import FileService from "@/services/file-service"
import { MapProvider } from "@/services/mapProvider"

import SkeletonLoader from "@/components/SkeletonLoader.vue"
import LayoutHost from "@/components/CaseDetails/Layout/LayoutHost.vue"
import DataForm from "@/components/Config/DataForm.vue"

import VuePerfectScrollbar from "vue-perfect-scrollbar"

import { CaseDetails, TemplateDetails } from "@/types"

import ScheduleEditService from "@/services/scheduleEditService"

import { Schedule } from "@/types/schedule"
import { cloneDeep } from "lodash"
import { CurrentUser } from "@/types/user"
import { TenantFeatures } from "@/types/tenantFeatures"
const caseStore = namespace("cases")

@Component({
	components: {
		"layout-host": LayoutHost,
		"desktop": () => import("@/components/CaseDetails/Layout/Desktop.vue"),
		"case-details": () => import("@/components/New/Cases/CaseDetails.vue"),
		"skeleton": SkeletonLoader,
		"data-form": DataForm,
		"scrollbar": VuePerfectScrollbar
	}
})
export default class ScheduleInstance extends Vue {
	@Provide("mapProvider") mapProvider: MapProvider = new MapProvider()
	@Provide("fileService") fileService: FileService = new FileService(this.caseFilesChanged)

	@Prop({ default: null })
	value!: {
		schedule: Schedule,
		date: Date
	} | null

	@Prop({ default: true })
	allowScheduleEdit!: boolean

	@Getter("getMe") public me!: CurrentUser
	@caseStore.Action public detailsLoad!: () => Promise<void>;

	@Getter("getUseV2")
	private readonly shouldUseNewInterface!: boolean;

	@Getter("getCaseName") getCaseName!: string;
	@Getter("getRecordName") getRecordName!: string;
	@Getter("getIsFeatureEnabled") getIsFeatureEnabled!: (feature: TenantFeatures) => boolean;
	@State tenantSettings!: { [key: string]: boolean } | null

	private currentCase: CaseDetails | null = null
	private currentTemplate: TemplateDetails | null = null

	private templates: { [key:string]: TemplateDetails } = {}

	private scheduleView: boolean = false

	private editSchedule: Schedule | null = null
	private scheduleEdit: ScheduleEditService | null = new ScheduleEditService()

	saving: boolean = false
	valid: boolean = true

	get open (): boolean {
		return !!this.value
	}

	set open (value: boolean) {
		if (!value) {
			this.scheduleView = false
			this.$emit("input", null)
		}
	}

	get caseTitle () {
		if (this.value) {
			return this.value.schedule.title
		}
		if (!this.currentCase) return ""
		return `${this.currentCase.templateTitle} ${this.currentCase.isCase ? this.getCaseName : this.getRecordName}`
	}

	get canEditSchedules () {
		return this.me && (this.me.editData || (this.me.extendedPermissions && this.me.extendedPermissions["scheduling"]))
	}

	private get scheduleColumns () {
		if (!this.scheduleEdit) return []

		return this.scheduleEdit.columns.filter((c: any) => {
			if (c.key === "endTime") {
				return !this.tenantSettings || !this.tenantSettings["scheduleHideTimeSpan"]
			}
			if (c.key === "bulkCreateForTeams") {
				return !this.tenantSettings || !this.tenantSettings["scheduleHideBulkCreate"]
			}
			if (c.key === "nextRunTimes") {
				return this.getIsFeatureEnabled(TenantFeatures.ScheduleDisplayRunTimes)
			}
			return true
		})
	}

	@Watch("value")
	async valueUpdated (newValue: { schedule: Schedule, date: Date } | null) {
		if (newValue && newValue.schedule) {
			this.detailsLoad()
			this.editSchedule = cloneDeep(newValue.schedule)

			api.scheduleInstance(newValue.schedule.id!, newValue.date).then((scheduleInstance) => {
				this.currentCase = scheduleInstance
				if (scheduleInstance.templateId) {
					// Load template - use cached values if present
					if (this.templates[scheduleInstance.templateId]) {
						this.currentTemplate = this.templates[scheduleInstance.templateId]
					} else {
						api.templateDetails(scheduleInstance.templateId!).then(template => {
							this.currentTemplate = this.templates[scheduleInstance.templateId!] = template
						})
					}
				}
			})
		} else {
			this.currentCase = null
			this.editSchedule = null
			this.currentTemplate = null
		}
	}

	mounted () {
		this.scheduleEdit!.load(this.me)
		this.valueUpdated(this.value)
	}

	private close () {
		this.$emit("input", null)
	}

	private caseFilesChanged () {
	}

	private async save () {
		if (this.scheduleView) {
			if (this.value && this.editSchedule) {
				this.scheduleEdit!.saveSchedule(this.editSchedule)
				this.$emit("scheduleUpdated", this.editSchedule)
			}
		}

		this.$emit("input", null)
		this.scheduleView = false
	}
}

